import React from 'react';

import { observer } from 'mobx-react';
import { Alert, TitleWithDescription, InfoBox } from '@infoods/lifebase-ui';
import { useForm } from 'react-hook-form';

import classes from './ChooseChallengeStep.module.scss';
import { AccountDetailsWidget } from '../../../../../../../customer-management';
import { ChallengeInfoCard } from './ChallengeInfoCard/ChallengeInfoCard';
import { DivisionPicker } from './DivisionPicker/DivisionPicker';
import { isPromoPriceLimited, PromoCodeCard } from '../../../../../../../payment';
import { ButtonRow } from '../../../../components/ButtonRow/ButtonRow';
import { CurrentPlanWidget } from './CurrentPlanWidget/CurrentPlanWidget';
import { challengeSignUpStore } from '../../../../../../stores/challengeSignUpStore';
import { DivisionFormValues } from './ChooseChallengeStep.settings';
import { useChallengeSubscription } from './hooks/useChallengeSubscription';

const ChooseChallengeStep = observer(() => {
  const { control, handleSubmit } = useForm<DivisionFormValues>();

  const {
    customer,
    entityData,
    challengePaymentInfo,
    nutritionSubscriptionInfo,
    isLifeBaseWithoutActiveSubscription,
  } = challengeSignUpStore;

  const { handleApplyPromoCode, handleSubscribeToChallenge } = useChallengeSubscription();

  return (
    <>
      <div className={classes.container}>
        <div className={classes.section}>
          {customer && (
            <AccountDetailsWidget
              firstName={customer.firstName}
              lastName={customer.lastName}
              entityName={entityData?.entityName}
              inbodyId={customer.inbodyId}
              dateOfBirth={customer.dateOfBirth}
              gender={customer.gender}
            />
          )}
          {nutritionSubscriptionInfo?.subscription && (
            <CurrentPlanWidget
              subscriptionName={nutritionSubscriptionInfo.subscription.name}
              billingPeriodType={nutritionSubscriptionInfo.subscription.billingPeriodType}
            />
          )}
        </div>
        <div className={classes.section}>
          <InfoBox
            contentClassName={classes.challengesCardContent}
            customMargin
            title={
              <div className={classes.title}>
                <span className={classes.iconText}>🏆</span>
                <TitleWithDescription
                  title="Challenges"
                  description="A challenge is a stepping stone to a lifetime of success"
                />
              </div>
            }
          >
            {isLifeBaseWithoutActiveSubscription && (
              <Alert severity="error" size="small">
                Reactivating your Nutrition Subscription is required. After purchasing the
                challenge ticket, contact your coach to receive a reactivation link.
              </Alert>
            )}
            <div className={classes.challengeItem}>
              <ChallengeInfoCard />
              {challengePaymentInfo &&
                isPromoPriceLimited(
                  challengePaymentInfo.pricePromo,
                  challengePaymentInfo.priceFinal,
                ) && (
                  <Alert severity="info" size="small">
                    The minimum charge is $0.50 due to Stripe processing fees.
                    <br />
                    We cannot process payments below this amount because of payment system
                    policies
                  </Alert>
                )}
            </div>
            <DivisionPicker control={control} />
          </InfoBox>
          <PromoCodeCard onApplyPromoCode={handleApplyPromoCode} />
        </div>
      </div>
      <ButtonRow yesHandler={handleSubmit(handleSubscribeToChallenge)} />
    </>
  );
});

export { ChooseChallengeStep };
